import { saveAs } from "file-saver";

import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { sunoCommsApi } from "../api-base";
import {
  ContactExportFormat,
  ContactExportJob,
  ContactImportJob,
  ContactImportJobSummary,
  ContactIoDownload
} from "../types/contact-io.type";
import { ContactsFilter } from "../types/lead-contact.type";
import { buildExportParams } from "../utils/contact-export-filters.util";

const TEMPLATE_FILENAMES: Record<ContactExportFormat, string> = {
  csv: "suno-contact-import-template.csv",
  xlsx: "suno-contact-import-template.xlsx"
};

/**
 * Reads the filename out of Content-Disposition, falling back to a fixed name.
 *
 * The fallback is the normal path, not the edge case: Suno Comms is served from a different origin
 * than the app (VITE_SUNO_COMMS_API, no dev proxy), so this header is invisible to JS unless the
 * server sends `Access-Control-Expose-Headers: Content-Disposition`.
 */
const readAttachmentFilename = (response: Response, fallback: string) => {
  const disposition = response.headers.get("Content-Disposition");
  if (!disposition) return fallback;

  const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const parsed = match?.[1]?.replace(/['"]/g, "").trim();

  return parsed || fallback;
};

export const contactIoEndpoints = sunoCommsApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // ── Import ────────────────────────────────────────────────────────────────
    uploadContactImport: builder.mutation<ContactImportJob, File>({
      query: (file) => {
        const formData = new FormData();
        // Field name is `file`, singular. Content-Type is deliberately unset so the browser
        // supplies the multipart boundary.
        formData.append("file", file);

        return {
          url: `/contacts/imports`,
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["ContactImports"]
    }),

    getContactImport: builder.query<ContactImportJob, string>({
      query: (jobId) => `/contacts/imports/${jobId}`,
      providesTags: (_result, _error, jobId) => [
        { type: "ContactImport", id: jobId }
      ]
    }),

    getContactImports: builder.query<ContactImportJobSummary[], void>({
      query: () => `/contacts/imports`,
      providesTags: ["ContactImports"]
    }),

    // Nothing is written to contacts until this call, and it only flips the job to `importing` —
    // rows land later. So LeadContacts is NOT invalidated here; the import hook does it when the
    // job reaches `completed`, otherwise the list refetches against zero new rows.
    confirmContactImport: builder.mutation<ContactImportJob, string>({
      query: (jobId) => ({
        url: `/contacts/imports/${jobId}/confirm`,
        method: "POST"
      }),
      invalidatesTags: (_result, _error, jobId) => [
        { type: "ContactImport", id: jobId },
        "ContactImports"
      ]
    }),

    // Presigned link, minted per request and valid for an hour. Never cached, and fetched on click
    // rather than on render.
    getContactImportProblems: builder.query<ContactIoDownload, string>({
      query: (jobId) => `/contacts/imports/${jobId}/problems`,
      keepUnusedDataFor: 0
    }),

    // Returns the file itself rather than JSON, so the download happens in the responseHandler and
    // nothing is written to the store. A mutation, not a query, to keep Blobs out of the cache.
    downloadImportTemplate: builder.mutation<null, ContactExportFormat>({
      query: (format) => ({
        url: `/contacts/import-template?format=${format}`,
        method: "GET",
        cache: "no-cache",
        responseHandler: async (response) => {
          // fetchBaseQuery runs responseHandler for error responses too, then branches on
          // response.ok to decide data-vs-error. Without this guard a 400 would download its own
          // JSON error body as a spreadsheet.
          if (!response.ok) {
            return response.json().catch(() => null);
          }

          const blob = await response.blob();
          saveAs(
            blob,
            readAttachmentFilename(response, TEMPLATE_FILENAMES[format])
          );

          return null;
        }
      })
    }),

    // ── Export ────────────────────────────────────────────────────────────────
    startContactExport: builder.mutation<
      ContactExportJob,
      { format: ContactExportFormat; filters?: Partial<ContactsFilter> }
    >({
      query: ({ format, filters }) => ({
        // buildExportParams applies the supported-filter allowlist. Doing it here rather than at the
        // call site means no caller can leak the contact list's limit/offset into an export.
        url: `/contacts/exports${formatValidQueryParamUrlCommaSeparated(
          { format, ...buildExportParams(filters) },
          true
        )}`,
        method: "POST"
      })
    }),

    // download_url is minted fresh on every call and only present once completed — refetch the job
    // instead of caching the link.
    getContactExport: builder.query<ContactExportJob, string>({
      query: (jobId) => `/contacts/exports/${jobId}`,
      keepUnusedDataFor: 0
    })
  })
});

export const {
  useUploadContactImportMutation,
  useGetContactImportQuery,
  useLazyGetContactImportsQuery,
  useConfirmContactImportMutation,
  useLazyGetContactImportProblemsQuery,
  useDownloadImportTemplateMutation,
  useStartContactExportMutation,
  useGetContactExportQuery
} = contactIoEndpoints;
